import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Check, Dumbbell, HeartPulse, LayoutList, Salad, Watch, Weight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Chip, Loader, PageHeader, Reveal } from "@/components/common";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { MealLogDialog } from "@/components/MealLogDialog";
import { MealReplaceDialog } from "@/components/MealReplaceDialog";
import { useDeviationAdapt } from "@/components/AiPlanDialog";
import { MealExtraDialog } from "@/components/MealExtraDialog";
import { todayIso } from "@/lib/dates";
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const startOfWeek = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
};

const startOfMonth = (iso) => `${iso.slice(0, 7)}-01`;

const endOfMonth = (iso) => {
  const d = new Date(`${iso.slice(0, 7)}-01T12:00:00`);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
};

const rangeFor = (range, anchor) => {
  if (range === "day") return { from: anchor, to: anchor };
  if (range === "week") {
    const from = startOfWeek(anchor);
    return { from, to: addDays(from, 6) };
  }
  return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
};

const shiftAnchor = (range, anchor, dir) => {
  if (range === "day") return addDays(anchor, dir);
  if (range === "week") return addDays(anchor, dir * 7);
  const d = new Date(`${anchor.slice(0, 7)}-01T12:00:00`);
  d.setMonth(d.getMonth() + dir);
  return d.toISOString().slice(0, 10);
};

const rangeLabel = (range, from, to) => {
  if (range === "day") return fmtDate(from);
  if (range === "week") return `${fmtDate(from)} – ${fmtDate(to)}`;
  const d = new Date(`${from}T12:00:00`);
  return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
};

const dayMarkers = (day) => {
  if (!day) return { train: false, food: false, care: false, other: false };
  return {
    train: !!day.workout,
    food: (day.meals || []).length > 0,
    care: (day.symptoms || []).length > 0,
    other: (day.imported_activities || []).length > 0 || (day.weights || []).length > 0 || !!day.feedback?.completed,
  };
};

const DayCard = ({
  day,
  isToday,
  dayRef,
  canTrain,
  canNutrition,
  onTrainFeedback,
  onTrainSkip,
  onTrainUndo,
  onMealLog,
  onMealUnmark,
  onMealExtra,
  onMealExtraRemove,
  busy,
}) => {
  const w = day.workout;
  const acts = w?.activities || [];
  const mealsDone = new Set(day.meals_done || []);
  const mealsSkipped = new Set(day.meals_skipped || []);
  const future = day.date > todayIso();
  const hasAnything = w || (day.meals || []).length || day.symptoms?.length || day.imported_activities?.length || day.weights?.length;
  if (!hasAnything && !(canNutrition && !future)) return null;
  return (
    <Reveal
      className={`rounded-3xl border p-5 scroll-mt-24 ${isToday ? "border-ink bg-stone-50 ring-2 ring-ink/15" : "border-stone-200 bg-white"}`}
      data-testid={`agenda-day-${day.date}`}
    >
      <div ref={dayRef} />
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="eyebrow text-stone-500">{day.label || fmtDate(day.date)}</p>
          <h2 className="mt-0.5 text-lg font-extrabold text-ink">
            {fmtDate(day.date)}
            {isToday && <span className="ml-2 rounded-full bg-ink px-2.5 py-0.5 text-xs font-semibold text-white align-middle">Hoy</span>}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          {day.feedback?.completed && <span className="rounded-full bg-train-soft px-2.5 py-1 text-train-ink">Entreno ✓</span>}
          {mealsDone.size >= 2 && <span className="rounded-full bg-food-soft px-2.5 py-1 text-food-ink">Comidas ✓</span>}
        </div>
      </div>

      {w && (
        <div className="mt-4 rounded-2xl bg-train-soft/70 p-4" data-testid={`agenda-workout-${day.date}`}>
          <p className="flex items-center gap-2 text-sm font-bold text-train-ink"><Dumbbell className="h-4 w-4" /> Entrenamiento</p>
          <p className="mt-1 font-semibold text-ink">{w.title}</p>
          {w.replaced_from_title && <p className="text-xs text-train-ink">En vez de: {w.replaced_from_title}</p>}
          <p className="mt-0.5 text-sm text-stone-600">{w.minutes} min{w.notes ? ` · ${w.notes}` : ""}</p>
          {acts.length > 0 ? (
            <ul className="mt-2 space-y-1 text-sm text-stone-700">
              {acts.map((a, i) => (
                <li key={i}>· {a.title} ({a.sport}, {a.minutes} min{a.distance_km != null ? `, ${a.distance_km} km` : ""})</li>
              ))}
            </ul>
          ) : (
            <ul className="mt-2 space-y-1 text-sm text-stone-700">
              {(w.items || []).slice(0, 6).map((it, i) => <li key={i}>· {it.exercise}</li>)}
            </ul>
          )}
          {day.feedback?.completed && (
            <p className="mt-2 text-xs text-stone-500">Dolor {day.feedback.pain}/10 · RPE {day.feedback.rpe}/10 · ★ {day.feedback.rating}</p>
          )}
          {day.feedback?.skipped && !day.feedback?.completed && (
            <p className="mt-2 text-xs font-semibold text-stone-500">
              {day.feedback.source === "auto" ? "Cerrada al pasar el día — puedes corregirlo." : "Sesión no realizada — puedes corregirlo."}
            </p>
          )}
          {canTrain && !future && w && day.feedback?.completed && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => onTrainUndo?.(day.date, "edit", w)}
                data-testid={`agenda-train-edit-${day.date}`}
                className="rounded-full bg-train px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
              >
                Cambiar registro
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => onTrainUndo?.(day.date, "clear")}
                data-testid={`agenda-train-undo-${day.date}`}
                className="rounded-full border border-stone-300 bg-white px-4 py-2 text-xs font-semibold text-stone-600 disabled:opacity-60"
              >
                Deshacer
              </button>
            </div>
          )}
          {canTrain && !future && w && !day.feedback?.completed && (
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => onTrainFeedback(day.date, "planned", w)}
                data-testid={`agenda-train-done-${day.date}`}
                className="rounded-full bg-train py-2.5 text-xs font-semibold text-white disabled:opacity-60"
              >
                {day.feedback?.skipped ? "Sí entrené (planeado)" : "Marcar como hecho"}
              </button>
              <button
                type="button"
                disabled={busy || w.kind === "descanso"}
                onClick={() => onTrainFeedback(day.date, "actual", w)}
                data-testid={`agenda-train-alt-${day.date}`}
                className="rounded-full border-2 border-train bg-white py-2.5 text-xs font-semibold text-train-ink disabled:opacity-60"
              >
                Hice otra cosa
              </button>
              {w.kind !== "descanso" && !day.feedback?.skipped && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onTrainSkip(day.date)}
                  data-testid={`agenda-train-skip-${day.date}`}
                  className="sm:col-span-2 rounded-full py-2 text-xs font-semibold text-stone-500 hover:bg-white/60"
                >
                  No entrené nada
                </button>
              )}
              {day.feedback?.skipped && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onTrainUndo?.(day.date, "clear")}
                  data-testid={`agenda-train-unskip-${day.date}`}
                  className="sm:col-span-2 rounded-full py-2 text-xs font-semibold text-stone-500 hover:bg-white/60"
                >
                  Quitar omisión
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {((day.meals || []).length > 0 || (canNutrition && !future)) && (
        <div className="mt-3 rounded-2xl bg-food-soft/70 p-4" data-testid={`agenda-meals-${day.date}`}>
          <p className="flex items-center gap-2 text-sm font-bold text-food-ink"><Salad className="h-4 w-4" /> Comidas</p>
          <ul className="mt-2 space-y-1.5 text-sm">
            {(day.meals || []).map((m) => {
              const done = mealsDone.has(m.slot);
              const skipped = mealsSkipped.has(m.slot);
              const isExtra = !!m.extra;
              return (
                <li key={m.slot} className="flex gap-2">
                  {canNutrition && !future && !isExtra ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => (done ? onMealUnmark(day.date, m.slot) : onMealLog(day.date, m))}
                      data-testid={`agenda-meal-mark-${day.date}-${m.slot}`}
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${done ? "border-food bg-food text-white" : "border-stone-300 bg-white hover:border-food"}`}
                      aria-label={done ? "Quitar registro" : "Registrar comida"}
                    >
                      {done && <Check className="h-3 w-3" />}
                    </button>
                  ) : (
                    <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border ${done || isExtra ? "border-food bg-food" : "border-stone-300"}`} />
                  )}
                  <span className={`min-w-0 flex-1 ${skipped ? "opacity-70 line-through" : ""}`}>
                    <span className="font-medium text-ink">{m.label}:</span> {m.name}
                    {isExtra && <span className="text-xs font-semibold text-food-ink"> · extra</span>}
                    {skipped && <span className="text-xs font-semibold text-stone-500"> · No realizada</span>}
                    {m.actual && m.replaced_from && <span className="text-xs text-food-ink"> (en vez de {m.replaced_from})</span>}
                    <span className="text-stone-500"> · {m.kcal} kcal</span>
                    {canNutrition && !future && isExtra && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onMealExtraRemove?.(day.date, m.slot)}
                        data-testid={`agenda-meal-extra-remove-${day.date}-${m.slot}`}
                        className="ml-2 text-xs font-semibold text-stone-500 hover:text-ink"
                      >
                        Quitar
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          {canNutrition && !future && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onMealExtra?.(day.date)}
              data-testid={`agenda-meal-extra-${day.date}`}
              className="mt-3 w-full rounded-full border border-dashed border-food/50 bg-white py-2 text-xs font-semibold text-food-ink"
            >
              + Añadir extra (cervezas, picoteo…)
            </button>
          )}
        </div>
      )}

      {(day.symptoms || []).length > 0 && (
        <div className="mt-3 rounded-2xl bg-care-soft/70 p-4" data-testid={`agenda-symptoms-${day.date}`}>
          <p className="flex items-center gap-2 text-sm font-bold text-care-ink"><HeartPulse className="h-4 w-4" /> Dolencias</p>
          <ul className="mt-2 space-y-1 text-sm text-stone-700">
            {day.symptoms.map((s, i) => (
              <li key={i}>· {s.ailment_name || s.zone || "Síntoma"} — dolor {s.pain}/10{s.notes ? `: ${s.notes}` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      {(day.imported_activities || []).length > 0 && (
        <div className="mt-3 rounded-2xl bg-stone-50 p-4" data-testid={`agenda-imported-${day.date}`}>
          <p className="flex items-center gap-2 text-sm font-bold text-stone-700"><Watch className="h-4 w-4" /> Actividad importada</p>
          <ul className="mt-2 space-y-1 text-sm text-stone-700">
            {day.imported_activities.map((a, i) => (
              <li key={i}>· {a.name || a.sport}{a.duration_min ? ` · ${a.duration_min} min` : ""}{a.distance_km ? ` · ${a.distance_km} km` : ""} <span className="text-xs text-stone-400">({a.provider})</span></li>
            ))}
          </ul>
        </div>
      )}

      {(day.weights || []).length > 0 && (
        <div className="mt-3 rounded-2xl bg-streak-soft/70 p-4">
          <p className="flex items-center gap-2 text-sm font-bold text-streak-ink"><Weight className="h-4 w-4" /> Peso</p>
          <ul className="mt-2 space-y-1 text-sm text-stone-700">
            {day.weights.map((wrow, i) => <li key={i}>· {wrow.weight_kg} kg</li>)}
          </ul>
        </div>
      )}
    </Reveal>
  );
};

const CalendarGrid = ({ from, to, byDate, selectedDate, onSelect }) => {
  const today = todayIso();
  // Expand to full weeks covering the month/range
  const gridStart = startOfWeek(from);
  const gridEnd = (() => {
    const e = startOfWeek(to);
    return addDays(e, 6);
  })();
  const cells = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) cells.push(d);
  const inRange = (iso) => iso >= from && iso <= to;

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-4 sm:p-5" data-testid="agenda-calendar-grid">
      <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wide text-stone-400">
        {WEEKDAYS.map((w) => <div key={w}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((iso) => {
          const day = byDate[iso];
          const m = dayMarkers(day);
          const isToday = iso === today;
          const selected = iso === selectedDate;
          const muted = !inRange(iso);
          const dayNum = Number(iso.slice(8, 10));
          return (
            <button
              key={iso}
              type="button"
              disabled={muted}
              onClick={() => onSelect(iso)}
              data-testid={`agenda-cal-cell-${iso}`}
              className={`relative flex min-h-[4.5rem] flex-col items-start rounded-2xl border p-1.5 text-left transition-colors sm:min-h-[5.25rem] sm:p-2 ${
                muted ? "border-transparent opacity-30" : "border-stone-100 hover:border-stone-300 hover:bg-stone-50"
              } ${isToday ? "border-ink bg-ink text-white hover:bg-ink hover:border-ink" : ""} ${
                selected && !isToday ? "border-train bg-train-soft/80 ring-1 ring-train/30" : ""
              }`}
            >
              <span className={`text-xs font-bold sm:text-sm ${isToday ? "text-white" : "text-ink"}`}>{dayNum}</span>
              {!muted && (
                <div className="mt-auto flex flex-wrap gap-0.5">
                  {m.train && <span className={`h-1.5 w-1.5 rounded-full ${isToday ? "bg-white" : "bg-train"}`} title="Entreno" />}
                  {m.food && <span className={`h-1.5 w-1.5 rounded-full ${isToday ? "bg-white/80" : "bg-food"}`} title="Comidas" />}
                  {m.care && <span className={`h-1.5 w-1.5 rounded-full ${isToday ? "bg-white/60" : "bg-care"}`} title="Dolencias" />}
                  {m.other && <span className={`h-1.5 w-1.5 rounded-full ${isToday ? "bg-white/40" : "bg-stone-400"}`} title="Otros" />}
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-stone-500">
        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-train" /> Entreno</span>
        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-food" /> Comidas</span>
        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-care" /> Dolencias</span>
        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-stone-400" /> Otros</span>
      </div>
    </div>
  );
};

export default function Agenda() {
  const { me } = useAuth();
  const canTrain = !!me?.modules?.training;
  const canNutrition = !!me?.modules?.nutrition;
  const [layout, setLayout] = useState("list"); // list | calendar
  const [range, setRange] = useState("week"); // day | week | month
  const [anchor, setAnchor] = useState(todayIso);
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prefsReady, setPrefsReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mealLog, setMealLog] = useState(null);
  const [mealReplace, setMealReplace] = useState(null);
  const [mealExtraDate, setMealExtraDate] = useState(null);
  const [trainCtx, setTrainCtx] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [sports, setSports] = useState([]);
  const [preferredSports, setPreferredSports] = useState([]);
  const todayRef = useRef(null);
  const detailRef = useRef(null);
  const today = todayIso();
  const { from, to } = useMemo(() => rangeFor(range, anchor), [range, anchor]);

  const reload = useCallback(() => {
    return api.get(`/agenda?from_date=${from}&to_date=${to}`).then((r) => setData(r.data));
  }, [from, to]);
  const { trigger: triggerAdapt, dialog: adaptDialog } = useDeviationAdapt(reload);

  useEffect(() => {
    if (!canTrain) return;
    api.get("/plan/catalog").then((r) => {
      setSports(r.data.sports || []);
      setPreferredSports(r.data.preferred_sports || []);
    }).catch(() => {});
  }, [canTrain]);

  useEffect(() => {
    api.get("/profile").then((r) => {
      const prefs = r.data?.ui_prefs || {};
      if (prefs.agenda_layout === "list" || prefs.agenda_layout === "calendar") setLayout(prefs.agenda_layout);
      if (prefs.agenda_range === "day" || prefs.agenda_range === "week" || prefs.agenda_range === "month") setRange(prefs.agenda_range);
    }).catch(() => {}).finally(() => setPrefsReady(true));
  }, []);

  const persistPrefs = (patch) => {
    api.patch("/profile/ui-prefs", patch).catch(() => {});
  };

  const changeLayout = (next) => {
    setLayout(next);
    persistPrefs({ agenda_layout: next });
  };
  const changeRange = (next) => {
    setRange(next);
    persistPrefs({ agenda_range: next });
  };

  useEffect(() => {
    if (!prefsReady) return;
    setLoading(true);
    api.get(`/agenda?from_date=${from}&to_date=${to}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [from, to, prefsReady]);

  const byDate = useMemo(() => {
    const map = {};
    (data?.days || []).forEach((d) => { map[d.date] = d; });
    return map;
  }, [data]);

  // Lista ordenada: más reciente arriba, pero scroll a hoy
  const listDays = useMemo(() => {
    const days = [...(data?.days || [])].sort((a, b) => b.date.localeCompare(a.date));
    return days;
  }, [data]);

  useEffect(() => {
    if (loading || layout !== "list") return;
    const t = requestAnimationFrame(() => {
      todayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(t);
  }, [loading, layout, listDays, from, to]);

  useEffect(() => {
    if (loading || layout !== "calendar") return;
    // Al abrir calendario en mes que incluye hoy, anclar selección a hoy
    if (today >= from && today <= to) setSelectedDate(today);
  }, [loading, layout, from, to, today]);

  const selectedDay = byDate[selectedDate];

  const goToday = () => {
    setAnchor(today);
    setSelectedDate(today);
    if (layout === "list") {
      setTimeout(() => todayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } else {
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  };

  const openTrainFeedback = (date, mode, workout) => {
    setTrainCtx({ date, mode, workout });
    setFeedbackOpen(true);
  };

  const completeTrain = async (fb) => {
    if (!trainCtx) return;
    setBusy(true);
    try {
      const planned = trainCtx.workout?.title;
      const isAlt = trainCtx.mode === "actual" || !!(fb.actual_activities?.length);
      const { data } = await api.post(`/plan/days/${trainCtx.date}/complete`, fb);
      toast.success(data.adjusted_days ? `Sesión guardada · ${data.adjusted_days} día(s) ajustados` : "Sesión guardada");
      const date = trainCtx.date;
      setFeedbackOpen(false);
      setTrainCtx(null);
      await reload();
      if (isAlt) {
        const actual = (fb.actual_activities || []).map((a) => a.title).filter(Boolean).join(" · ") || "otra actividad";
        triggerAdapt({ kind: "workout_alt", date, planned, actual });
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const skipTrain = async (date) => {
    setBusy(true);
    try {
      const planned = byDate[date]?.workout?.title;
      const { data } = await api.post(`/plan/days/${date}/skip`, {});
      toast.success(data.adjusted_days ? `Registrado · ${data.adjusted_days} día(s) ajustados` : "Registrado como no realizado");
      await reload();
      triggerAdapt({ kind: "workout_skip", date, planned });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const undoTrain = async (date, mode = "clear", workout = null) => {
    setBusy(true);
    try {
      await api.post(`/plan/days/${date}/uncomplete`);
      await reload();
      if (mode === "edit" && workout) {
        openTrainFeedback(date, "planned", workout);
      } else {
        toast.success("Registro de entreno deshecho");
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const markMealPlanned = async () => {
    if (!mealLog) return;
    setBusy(true);
    try {
      await api.post(`/plan/days/${mealLog.date}/meals/${mealLog.meal.slot}/toggle`);
      toast.success("Comida registrada");
      setMealLog(null);
      await reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const skipMeal = async () => {
    if (!mealLog) return;
    setBusy(true);
    try {
      const { date, meal } = mealLog;
      await api.post(`/plan/days/${date}/meals/${meal.slot}/skip`);
      setMealLog(null);
      await reload();
      triggerAdapt({ kind: "meal_skip", date, slot: meal.slot });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const unmarkMeal = async (date, slot) => {
    setBusy(true);
    try {
      await api.post(`/plan/days/${date}/meals/${slot}/toggle`);
      await reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitMealReplace = async (body) => {
    if (!mealReplace) return;
    setBusy(true);
    try {
      const { date, meal } = mealReplace;
      const { data } = await api.post(`/plan/days/${date}/meals/${meal.slot}/replace`, body);
      const done = new Set(data.day.meals_done || []);
      if (!done.has(meal.slot)) {
        await api.post(`/plan/days/${date}/meals/${meal.slot}/toggle`);
      }
      toast.success("Registrado lo que comiste");
      setMealReplace(null);
      await reload();
      triggerAdapt({
        kind: "meal_replace",
        date,
        slot: meal.slot,
        planned: meal.name,
        actual: body.custom_name || body.meal_name || "otra cosa",
      });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitMealExtra = async (body) => {
    if (!mealExtraDate) return;
    setBusy(true);
    try {
      await api.post(`/plan/days/${mealExtraDate}/meals/extra`, body);
      toast.success("Extra añadido al día");
      const date = mealExtraDate;
      setMealExtraDate(null);
      await reload();
      triggerAdapt({
        kind: "meal_extra",
        date,
        actual: body.name,
        kcal: body.kcal,
        time: body.time,
      });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const removeMealExtra = async (date, slot) => {
    setBusy(true);
    try {
      await api.delete(`/plan/days/${date}/meals/${slot}`);
      toast.success("Extra eliminado");
      await reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const dayCardProps = {
    canTrain,
    canNutrition,
    busy,
    onTrainFeedback: openTrainFeedback,
    onTrainSkip: skipTrain,
    onTrainUndo: undoTrain,
    onMealLog: (date, meal) => setMealLog({ date, meal }),
    onMealUnmark: unmarkMeal,
    onMealExtra: (date) => setMealExtraDate(date),
    onMealExtraRemove: removeMealExtra,
  };

  return (
    <div data-testid="agenda-page">
      <PageHeader
        eyebrow="Agenda"
        tone="ink"
        title="Tu historial"
        subtitle="Lista detallada o calendario. Tu preferencia se guarda en el perfil."
      >
        <div className="mt-4 flex flex-wrap gap-2">
          <Chip active={layout === "list"} onClick={() => changeLayout("list")} testid="agenda-layout-list">
            <span className="inline-flex items-center gap-1.5"><LayoutList className="h-3.5 w-3.5" /> Lista</span>
          </Chip>
          <Chip active={layout === "calendar"} onClick={() => changeLayout("calendar")} testid="agenda-layout-calendar">
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Calendario</span>
          </Chip>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {[["day", "Día"], ["week", "Semana"], ["month", "Mes"]].map(([id, label]) => (
            <Chip key={id} active={range === id} onClick={() => changeRange(id)} testid={`agenda-view-${id}`}>{label}</Chip>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button type="button" onClick={() => setAnchor((a) => shiftAnchor(range, a, -1))} data-testid="agenda-prev" className="rounded-full bg-stone-100 p-2 hover:bg-stone-200">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="flex items-center gap-2 text-sm font-semibold capitalize" data-testid="agenda-range-label">
            <CalendarDays className="h-4 w-4" /> {rangeLabel(range, from, to)}
          </p>
          <button type="button" onClick={() => setAnchor((a) => shiftAnchor(range, a, 1))} data-testid="agenda-next" className="rounded-full bg-stone-100 p-2 hover:bg-stone-200">
            <ChevronRight className="h-5 w-5" />
          </button>
          <button type="button" onClick={goToday} data-testid="agenda-today" className="ml-auto rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white">Hoy</button>
        </div>
      </PageHeader>

      {!prefsReady || loading ? <Loader /> : layout === "calendar" ? (
        <div className="space-y-4">
          <CalendarGrid
            from={from}
            to={to}
            byDate={byDate}
            selectedDate={selectedDate}
            onSelect={(iso) => {
              setSelectedDate(iso);
              setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
            }}
          />
          <div ref={detailRef} className="scroll-mt-24">
            {selectedDay ? (
              <DayCard day={selectedDay} isToday={selectedDay.date === today} {...dayCardProps} />
            ) : (
              <p className="rounded-3xl border border-dashed border-stone-300 p-6 text-center text-sm text-stone-600">
                {selectedDate === today ? "Hoy aún no tiene registros." : `Sin registros el ${fmtDate(selectedDate)}.`}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {!listDays.length && (
            <p className="rounded-3xl border border-dashed border-stone-300 p-8 text-center text-sm text-stone-600" data-testid="agenda-empty">
              No hay registros en este periodo.
            </p>
          )}
          {listDays.map((day) => (
            <DayCard
              key={day.date}
              day={day}
              isToday={day.date === today}
              dayRef={day.date === today ? todayRef : undefined}
              {...dayCardProps}
            />
          ))}
          {/* Si hoy está en el rango pero no hay card (sin datos), ancla invisible para scroll */}
          {today >= from && today <= to && !byDate[today] && (
            <div ref={todayRef} className="scroll-mt-24 rounded-3xl border-2 border-dashed border-ink/30 bg-stone-50 p-6 text-center" data-testid="agenda-today-empty">
              <p className="text-sm font-semibold text-ink">Hoy · {fmtDate(today)}</p>
              <p className="mt-1 text-sm text-stone-600">Aún no hay registros para hoy.</p>
            </div>
          )}
        </div>
      )}

      <MealLogDialog
        open={!!mealLog}
        onOpenChange={(o) => !o && setMealLog(null)}
        meal={mealLog?.meal}
        busy={busy}
        onPlanned={markMealPlanned}
        onOther={() => {
          if (!mealLog) return;
          setMealReplace(mealLog);
          setMealLog(null);
        }}
        onSkip={skipMeal}
      />
      <MealReplaceDialog
        open={!!mealReplace}
        onOpenChange={(o) => !o && setMealReplace(null)}
        meal={mealReplace?.meal}
        onSubmit={submitMealReplace}
        busy={busy}
      />
      <MealExtraDialog
        open={!!mealExtraDate}
        onOpenChange={(o) => !o && setMealExtraDate(null)}
        onSubmit={submitMealExtra}
        busy={busy}
      />
      <FeedbackDialog
        open={feedbackOpen}
        onOpenChange={(o) => { setFeedbackOpen(o); if (!o) setTrainCtx(null); }}
        onSubmit={completeTrain}
        busy={busy}
        plannedTitle={trainCtx?.workout?.title}
        plannedMinutes={trainCtx?.workout?.minutes}
        sports={sports}
        preferredSports={preferredSports}
        initialMode={trainCtx?.mode || "planned"}
      />
      {adaptDialog}
    </div>
  );
}
